import React, { useState, useRef, useEffect } from 'react';
import Modal from '../common/Modal';
import { useProfileStore } from '../../state/profileStore';
import { useNavigate } from 'react-router-dom';
import { handleProfileRedirect } from '../../services/profileUrlService';

const LoginModal = ({ isOpen, onClose, onForgotPassword, onSignupClick }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // 2FA state
  const [twoFAStep, setTwoFAStep] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState('');
  const codeInputRef = useRef(null);

  const { login, verify2FA } = useProfileStore();
  const navigate = useNavigate();

  // Auto-focus code input when 2FA step appears
  useEffect(() => {
    if (twoFAStep && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [twoFAStep]);

  const handleLoginSuccess = (user) => {
    onClose();
    const redirectUrl = handleProfileRedirect(user);
    navigate(redirectUrl, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        handleLoginSuccess(result.user);
      } else if (result.requires2FA) {
        // Password was correct — switch to 2FA code entry
        setPendingToken(result.pendingToken);
        setMaskedEmail(result.maskedEmail);
        setTwoFAStep(true);
        setError('');
      } else {
        setError(result.error || 'Failed to log in');
      }
    } catch (error) {
      setError('Failed to log in: ' + error.message);
    }
    setIsLoading(false);
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await verify2FA(pendingToken, twoFACode);
      if (result.success) {
        handleLoginSuccess(result.user);
      } else {
        setError(result.error || 'Invalid code');
      }
    } catch (error) {
      setError('Verification failed: ' + error.message);
    }
    setIsLoading(false);
  };

  const handleResendCode = async () => {
    setResending(true);
    setResendSuccess('');
    setError('');
    try {
      const res = await fetch('/api/auth/2fa/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pendingToken }),
        credentials: 'include',
      });
      const data = await res.json();
      if (data.ok) {
        setResendSuccess('New code sent to your email!');
        setTwoFACode('');
        setTimeout(() => setResendSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to resend code');
      }
    } catch {
      setError('Failed to resend code');
    }
    setResending(false);
  };

  const handleBackToLogin = () => {
    setTwoFAStep(false);
    setPendingToken(null);
    setMaskedEmail('');
    setTwoFACode('');
    setError('');
    setResendSuccess('');
  };

  // Handle code input — digits only, max 6
  const handleCodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setTwoFACode(val);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="space-y-6">
        {twoFAStep ? (
          /* ── 2FA Code Entry ── */
          <>
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-[#00aef7]/10 rounded-full flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-[#00aef7]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold">Two-Factor Authentication</h2>
              <p className="text-sm text-gray-500 mt-2">
                Enter the 6-digit code sent to <span className="font-medium">{maskedEmail}</span>
              </p>
            </div>

            {error && <div className="text-red-500 text-center text-sm">{error}</div>}
            {resendSuccess && <div className="text-green-600 text-center text-sm">{resendSuccess}</div>}

            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div>
                <input
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={twoFACode}
                  onChange={handleCodeChange}
                  placeholder="000000"
                  className="block w-full text-center text-2xl tracking-[0.5em] font-mono rounded-md border-gray-400 shadow-sm focus:border-[#00aef7] focus:ring-[#00aef7] px-3 py-3"
                  maxLength={6}
                />
              </div>

              <button
                type="submit"
                className="w-full bg-brand text-white p-2 rounded hover:bg-brand-dark transition-colors disabled:opacity-50"
                disabled={isLoading || twoFACode.length !== 6}
              >
                {isLoading ? 'Verifying...' : 'Verify & Log In'}
              </button>
            </form>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={handleBackToLogin}
                className="text-gray-500 hover:text-gray-700"
              >
                Back to login
              </button>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resending}
                className="text-[#00aef7] hover:text-[#0088c3] disabled:opacity-50"
              >
                {resending ? 'Sending...' : 'Resend code'}
              </button>
            </div>
          </>
        ) : (
          /* ── Normal Login Form ── */
          <>
            <h2 className="text-2xl font-bold text-center">Login</h2>
            {error && <div className="text-red-500 text-center">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Business Email</label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-400 shadow-sm focus:border-[#00aef7] focus:ring-[#00aef7] px-3 py-2"
                  placeholder="youremail@yourbusiness.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-400 shadow-sm focus:border-[#00aef7] focus:ring-[#00aef7] px-3 py-2"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showPassword ? "\uD83D\uDC41\uFE0F" : "\uD83D\uDC41\uFE0F\u200D\uD83D\uDDE8\uFE0F"}
                  </button>
                </div>
              </div>
              <div className="text-right">
                <button
                  type="button"
                  onClick={onForgotPassword}
                  className="text-brand hover:text-brand-dark text-sm"
                >
                  Forgot Password?
                </button>
              </div>

              <button
                type="submit"
                className="w-full bg-brand text-white p-2 rounded hover:bg-brand-dark transition-colors"
                disabled={isLoading}
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </button>
            </form>
            <div className="mt-4 text-center text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                onClick={() => {
                  onClose();
                  onSignupClick();
                }}
                className="text-[#00aef7] hover:text-[#0088c3]"
              >
                Sign Up here
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default LoginModal;
